import { useRef, useState } from "react"

import AssetService from "services/AssetService"
import toastMessages from "stores/toastMessages"
import ExistingLmidData from "models/ExistingLmidData"
import { ErrorCode } from "config/constants"
import validateLmid from "./util/validateLmid"

/**
 * Debounce-free LMID lookup as used by the LMID modals: validates the entered
 * value, aborts a running request when the input changes, and distinguishes
 * "found" (`data`), "not found" (`data === null`) and "no valid input yet"
 * (`data === undefined`).
 */
export default function useExistingLmidSearch() {
    const [isSearching, setIsSearching] = useState(false)
    const [existingLmidData, setExistingLmidData] = useState<ExistingLmidData | null | undefined>()
    const abortControllerRef = useRef<AbortController | null>(null)

    async function search(value: string) {
        const trimmed = value.trim()
        if (abortControllerRef.current) {
            abortControllerRef.current.abort()
            abortControllerRef.current = null
        }
        if (!validateLmid(trimmed)) {
            setExistingLmidData(undefined)
            setIsSearching(false)
            return
        }
        setIsSearching(true)
        const { controller, promise } = AssetService.requestExistingLmid({ search: trimmed })
        abortControllerRef.current = controller
        try {
            const response = await promise()
            setExistingLmidData(response)
            setIsSearching(false)
        } catch (e) {
            const error: Error = e
            if (error.message === ErrorCode.unknownError) {
                toastMessages.showSystemError()
            }
            if (error.message === ErrorCode.unknownError
                || error.message === ErrorCode.systemError) {
                setExistingLmidData(undefined)
                setIsSearching(false)
            }
            if (error.message === ErrorCode.notFound) {
                setExistingLmidData(null)
                setIsSearching(false)
            }
        }
    }

    function reset() {
        setExistingLmidData(undefined)
        setIsSearching(false)
    }

    return {
        search,
        reset,
        isSearching,
        existingLmidData,
    }
}
